import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, Trash2, Loader2, Clock, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { api } from './api';
import { MaternalBackLink, MaternalDisclaimer } from './_ui';

/**
 * Maternal · Appointments
 *
 * Simple, calm appointment tracker. No alerts, no countdown timers — just a
 * gentle list of what's coming up + a one-tap "What to prepare" hint
 * (suggested questions / things to bring) that sits inline.
 *
 * Backend:
 *   GET  /api/maternal/appointments
 *   POST /api/maternal/appointments {title, date(YYYY-MM-DD), notes?}
 *   DELETE /api/maternal/appointments/{id}
 */

const COMMON_TITLES = [
  '12-week scan',
  '20-week anomaly scan',
  'Glucose tolerance test',
  'GP / Midwife visit',
  '6-week postpartum check',
  'Mental wellness check-in',
];

const PREP_HINTS = [
  'Bring a list of any new symptoms since your last visit.',
  'Note any questions about food, sleep, or movement.',
  'Bring your maternity notes / app summary.',
  'Plan a calm 30 minutes before — hydrate, sit, breathe.',
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const MaternalAppointments = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [working, setWorking] = useState(false);
  const [form, setForm] = useState({ title: '', date: todayISO(), notes: '' });

  const refresh = async () => {
    try {
      const res = await api.appointments();
      setItems(res?.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const title = form.title.trim();
    const date = form.date;
    if (!title) { toast.error('Please give the appointment a name.'); return; }
    if (!date)  { toast.error('Please pick a date.'); return; }
    setWorking(true);
    try {
      await api.addAppt({ title, date, notes: form.notes.trim() || null });
      toast.success('Appointment saved.');
      setForm({ title: '', date: todayISO(), notes: '' });
      setAdding(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async (id) => {
    setWorking(true);
    try {
      await api.delAppt(id);
      setItems((prev) => prev.filter((r) => r.id !== id));
      toast.success('Appointment removed.');
    } catch {
      toast.error('Could not remove. Please try again.');
    } finally {
      setWorking(false);
    }
  };

  const today = todayISO();
  const upcoming = items.filter((a) => (a.date || '') >= today).sort((a,b) => (a.date || '').localeCompare(b.date || ''));
  const past = items.filter((a) => (a.date || '') < today).sort((a,b) => (b.date || '').localeCompare(a.date || ''));
  const next = upcoming[0];

  const fmtDate = (iso) => {
    try { return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };
  const daysUntil = (iso) => {
    try {
      const ms = new Date(iso + 'T00:00:00').getTime() - new Date(today + 'T00:00:00').getTime();
      const d = Math.round(ms / 86400000);
      if (d === 0) return 'today';
      if (d === 1) return 'tomorrow';
      return `in ${d} days`;
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50/40 via-white to-white pb-24" data-testid="maternal-appointments">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <MaternalBackLink />

        <header className="mb-5 flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center shrink-0">
              <Calendar className="h-5 w-5 text-white" />
            </span>
            <div>
              <p className="text-[11px] font-semibold text-rose-600 uppercase tracking-widest">Appointments</p>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">Visits & prep</h1>
            </div>
          </div>
          <button
            onClick={() => setAdding((v) => !v)}
            className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl px-3 py-2 transition-all shrink-0"
            data-testid="maternal-appointments-add-toggle"
          >
            <Plus className="h-4 w-4" />
            Add
          </button>
        </header>

        {/* Add form */}
        {adding && (
          <form
            onSubmit={handleAdd}
            className="rounded-2xl bg-white border-2 border-rose-100 shadow-sm p-4 mb-4 space-y-3"
            data-testid="maternal-appointments-form"
          >
            <div>
              <label htmlFor="appt-title" className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1 block">Visit name</label>
              <input
                id="appt-title"
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                maxLength={120}
                placeholder="E.g. 20-week anomaly scan"
                className="w-full rounded-xl border-2 border-gray-100 bg-white p-2.5 text-sm focus:border-rose-300 focus:outline-none transition-colors"
                data-testid="maternal-appointments-title"
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {COMMON_TITLES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, title: t }))}
                    className="text-[11px] text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-full px-2 py-0.5 transition-colors"
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor="appt-date" className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1 block">Date</label>
                <input
                  id="appt-date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  min={today}
                  className="w-full rounded-xl border-2 border-gray-100 bg-white p-2.5 text-sm focus:border-rose-300 focus:outline-none transition-colors"
                  data-testid="maternal-appointments-date"
                />
              </div>
              <div>
                <label htmlFor="appt-notes" className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-1 block">Notes (optional)</label>
                <input
                  id="appt-notes"
                  type="text"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  maxLength={500}
                  placeholder="Questions to ask, things to bring…"
                  className="w-full rounded-xl border-2 border-gray-100 bg-white p-2.5 text-sm focus:border-rose-300 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-1">
              <button
                type="button"
                onClick={() => setAdding(false)}
                className="text-sm font-semibold rounded-xl px-3 py-2 border-2 border-gray-200 hover:border-gray-300 text-gray-700 transition-all"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={working}
                className="text-sm font-bold rounded-xl px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white transition-all"
                data-testid="maternal-appointments-save"
              >
                {working && <Loader2 className="h-3.5 w-3.5 inline-block animate-spin mr-1.5" />}
                Save
              </button>
            </div>
          </form>
        )}

        {/* Next-up highlight */}
        {next && (
          <article className="rounded-2xl bg-white border-2 border-rose-100 shadow-sm p-5 mb-4" data-testid="maternal-appointments-next">
            <p className="text-[11px] font-semibold text-rose-700 uppercase tracking-widest mb-1">Next up</p>
            <h2 className="text-lg font-bold text-gray-900 leading-tight">{next.title}</h2>
            <p className="text-xs text-gray-600 mt-1 inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {fmtDate(next.date)} · {daysUntil(next.date)}
            </p>
            {next.notes && <p className="text-sm text-gray-700 leading-relaxed mt-2">{next.notes}</p>}
            <div className="mt-3 rounded-xl bg-rose-50 border border-rose-100 p-3">
              <p className="text-[10px] font-bold text-rose-800 uppercase tracking-wider mb-1.5">A gentle prep checklist</p>
              <ul className="space-y-1">
                {PREP_HINTS.map((h) => (
                  <li key={h} className="text-xs text-rose-900 flex items-start gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-rose-500 mt-1.5 shrink-0" aria-hidden="true" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </article>
        )}

        {/* Upcoming list */}
        <section className="mb-4" data-testid="maternal-appointments-upcoming">
          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2 px-1">Upcoming</p>
          {loading ? (
            <p className="text-sm text-gray-500 italic px-1">Loading…</p>
          ) : upcoming.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-white p-5 text-center">
              <Calendar className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-700 font-semibold">No upcoming visits yet</p>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed">Add your next scan, midwife visit, or postpartum check above.</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {upcoming.map((a) => (
                <li
                  key={a.id}
                  className="rounded-2xl bg-white border border-gray-100 shadow-sm p-3 flex items-start gap-3"
                  data-testid={`maternal-appointments-row-${a.id}`}
                >
                  <span className="w-9 h-9 rounded-lg bg-rose-50 text-rose-700 flex items-center justify-center shrink-0">
                    <Calendar className="h-4 w-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 leading-tight truncate">{a.title}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{fmtDate(a.date)} · {daysUntil(a.date)}</p>
                    {a.notes && <p className="text-xs text-gray-600 mt-1 leading-relaxed">{a.notes}</p>}
                  </div>
                  <button
                    onClick={() => handleDelete(a.id)}
                    disabled={working}
                    aria-label="Remove appointment"
                    className="shrink-0 text-rose-600 hover:bg-rose-50 rounded-lg p-1.5 transition-colors"
                    data-testid={`maternal-appointments-delete-${a.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Past list — kept compact */}
        {past.length > 0 && (
          <section data-testid="maternal-appointments-past">
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2 px-1">Past visits</p>
            <ul className="space-y-1.5">
              {past.slice(0, 6).map((a) => (
                <li
                  key={a.id}
                  className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2 flex items-start gap-2"
                >
                  <Clock className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 truncate">{a.title}</p>
                    <p className="text-[11px] text-gray-500">{fmtDate(a.date)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-5">
          <button
            onClick={() => navigate('/maternal-health/checkin')}
            className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold rounded-xl px-4 py-2.5 transition-all"
          >
            Log today's check-in
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <MaternalDisclaimer />
      </div>
    </div>
  );
};

export default MaternalAppointments;
